/**
 * Per-biome gradient environment — the "cheap GI" of the 2026-08-01 lighting
 * pass.
 *
 * WHAT IT REPLACES. The toy-plastic rig used to bake three's `RoomEnvironment`
 * through `PMREMGenerator` and hang the resulting cubemap on
 * `scene.environment`. That is a NEUTRAL studio lightbox: it gives the
 * clearcoat its broad wraparound highlight, but it is the SAME grey box in
 * every biome, so a car in a wheat field and the same car in a pine forest pick
 * up identical ambient. The playtest asked for "cheap GI that aligns with the
 * clearcoat direction" — i.e. the ambient a surface receives should carry the
 * colour of what is actually around it.
 *
 * WHAT THIS IS. A two-colour vertical gradient environment evaluated
 * ANALYTICALLY in the shader: sky colour above, ground colour below, blended
 * across the horizon. No texture, no PMREM, no render target — the whole
 * environment is `mix(ground, sky, smoothstep(-h, h, dir.y))`, so re-tinting it
 * per biome is two uniform writes per frame and costs nothing. Because the
 * environment IS what the clearcoat lobe samples (three's `EnvironmentNode`
 * evaluates the same node for radiance, irradiance AND clearcoat radiance), the
 * sheen on every candy surface now carries the biome's own sky/ground colours.
 * That is the requested "GI aligned with the clearcoat direction".
 *
 * WHY A CUSTOM NODE AND NOT A REBAKED PMREM. `scene.environment` is keyed by
 * TEXTURE identity inside the renderer's node cache, so swapping in a freshly
 * baked cubemap per biome rebuilds every material's shader — a multi-frame
 * hitch at exactly the moment the world is already streaming new props. A node
 * on `scene.environmentNode` is compiled ONCE; only its uniforms change.
 *
 * ROUGHNESS. A PMREM stores one prefiltered mip per roughness; this gradient is
 * so low-frequency that its "blurred" version is itself, so the node ignores
 * the requested texture level entirely. That is the whole reason it can be this
 * cheap, and the reason it is a good match for the look: the environment's job
 * here is a soft directional ambient, not a legible reflection.
 *
 * ORIENTATION. `EnvironmentNode` hands us a WORLD-space direction through the
 * builder context (the reflection vector for radiance, the world normal for
 * irradiance), so `dir.y` is up in world space in both cases.
 */

import { Color, Node } from "three/webgpu";
import { mix, nodeObject, normalWorld, smoothstep, uniform, vec3 } from "three/tsl";

/**
 * Half-width of the horizon blend, in units of `dir.y`. 0.55 spreads the
 * sky→ground transition across most of the sphere: a hard horizon line reads as
 * a two-tone reflection on the glossy surfaces (you can SEE the seam sweep
 * across a car roof as it turns), while a wide blend reads as light.
 */
const HORIZON_HALF = 0.55;

/**
 * How much of the ground colour survives into the SKY half.
 *
 * A pure two-colour sphere makes every upward-facing surface exactly the sky
 * tint, which over-saturates roofs in the strongly coloured biomes (wheat,
 * tunnel). Mixing a little of the opposite colour into each hemisphere is the
 * cheap stand-in for the multiple bounces a real GI solution would give.
 */
const CROSS_BLEED = 0.18;

/** The environment handle: the node to hang on the scene, plus its tint dial. */
export interface GradientEnvironment {
  /**
   * Assign to `scene.environmentNode`. Deliberately `unknown`: three types that
   * field as a `vec3`-typed node and this is a hand-rolled Node subclass, so
   * the call site does the one cast rather than this module lying about it.
   */
  readonly node: unknown;
  /**
   * Re-tint the environment. Cheap enough to call every frame — the biome
   * system does exactly that with its transition-blended colours.
   */
  setColors(sky: Color, ground: Color): void;
}

/**
 * The node itself. It reads the direction the lighting model asks for out of
 * the builder context (the same hook `PMREMNode` uses) and falls back to the
 * world normal if some future caller evaluates it outside an environment
 * context.
 */
class GradientEnvNode extends Node {
  static get type(): string {
    return "GradientEnvNode";
  }

  constructor(
    private readonly skyNode: ReturnType<typeof uniform>,
    private readonly groundNode: ReturnType<typeof uniform>,
  ) {
    super("vec3");
  }

  setup(builder: unknown): Node {
    const ctx = (builder as { context?: { getUV?: (n: unknown, b: unknown) => unknown } }).context;
    const dir = (ctx && ctx.getUV ? ctx.getUV(this, builder) : normalWorld) as {
      y: unknown;
    };
    // NOTE (same trap as plastic.ts `vehiclePaintMaterial`): the TSL builder
    // generics are ruinously expensive to infer through a chain of nested
    // mix/smoothstep calls — leaving them inferred took `tsc --noEmit` from
    // ~40 s to over five minutes on this one expression. Every node below is
    // deliberately erased to `never`/`unknown` so the checker stops unifying
    // node generics; the shapes are exercised at runtime by the visual pass.
    const t: unknown = smoothstep(-HORIZON_HALF, HORIZON_HALF, dir.y as never);
    const sky: unknown = vec3(this.skyNode as never);
    const ground: unknown = vec3(this.groundNode as never);
    // Bleed a little of each hemisphere into the other before the main blend —
    // see CROSS_BLEED.
    const skyMixed: unknown = mix(sky as never, ground as never, CROSS_BLEED);
    const groundMixed: unknown = mix(ground as never, sky as never, CROSS_BLEED);
    return mix(groundMixed as never, skyMixed as never, t as never) as unknown as Node;
  }
}

/**
 * Build the gradient environment. The returned node is a TSL node OBJECT (not
 * the bare class instance) because `EnvironmentNode` calls `.context(...)` on
 * whatever it is given.
 */
export function createGradientEnvironment(sky: Color, ground: Color): GradientEnvironment {
  const skyU = uniform(new Color().copy(sky));
  const groundU = uniform(new Color().copy(ground));
  const node: unknown = nodeObject(new GradientEnvNode(skyU, groundU) as never);
  return {
    node,
    setColors(s: Color, g: Color): void {
      (skyU.value as Color).copy(s);
      (groundU.value as Color).copy(g);
    },
  };
}
